import {
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { C, alpha } from '../lib/theme.js';

/** Mono uppercase eyebrow label. */
export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="eyebrow" style={style}>
      {children}
    </div>
  );
}

/** Page header: eyebrow + title + optional right-side actions. */
export function PageHeader({
  eyebrow,
  title,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 22,
      }}
    >
      <div>
        {eyebrow && <Eyebrow style={{ marginBottom: 9 }}>{eyebrow}</Eyebrow>}
        <h1 className="page-title">{title}</h1>
      </div>
      {actions && <div style={{ display: 'flex', gap: 9 }}>{actions}</div>}
    </div>
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost';
};
export function Button({ variant = 'ghost', className = '', ...rest }: BtnProps) {
  return <button className={`ui-btn ui-btn-${variant} ${className}`} {...rest} />;
}

export function Card({
  children,
  style,
  className = '',
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div className={`ui-card ${className}`} style={style}>
      {children}
    </div>
  );
}

const STATUS: Record<string, string> = {
  connected: C.good,
  active: C.good,
  success: C.good,
  ok: C.good,
  running: C.info,
  learning: C.watch,
  partial: C.watch,
  watch: C.watch,
  paused: C.faint,
  not_connected: C.faint,
  error: C.act,
  act: C.act,
};

/** Status pill with a colored dot; color resolved from a status keyword. */
export function StatusPill({ status, label }: { status: string; label?: string }) {
  const color = STATUS[status] ?? C.faint;
  const text = label ?? status.replace(/_/g, ' ');
  return (
    <span
      className="pill"
      style={{
        color,
        background: alpha(color, 13),
        border: `1px solid ${alpha(color, 34)}`,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {text}
    </span>
  );
}

/** Small square color dot (used for desire doors, legends). */
export function Dot({
  color,
  size = 8,
  round = true,
}: {
  color: string;
  size?: number;
  round?: boolean;
}) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: round ? '50%' : 2,
        background: color,
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
  );
}

/** Tiny inline sparkline from a list of 0..1 values. */
export function Sparkline({
  points,
  color,
  width = 120,
  height = 24,
}: {
  points: number[];
  color: string;
  width?: number;
  height?: number;
}) {
  const n = points.length;
  const path = points
    .map((v, i) => `${(i / (n - 1)) * width},${height - v * (height - 4) - 2}`)
    .join(' ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polyline points={path} fill="none" stroke={color} strokeWidth={1.75} />
    </svg>
  );
}

/**
 * Small circled "i" that reveals a plain-language explanation on hover/tap.
 * Rendered via a portal + fixed positioning so it never clips inside tables/cards.
 * Pass `title` for a bold heading above the body text.
 */
export function InfoTip({ text, title }: { text: string; title?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; top: number; bottom: number } | null>(null);
  const [nudge, setNudge] = useState<{ dx: number; dy: number } | null>(null);

  function show() {
    const r = ref.current?.getBoundingClientRect();
    if (r) {
      setNudge(null);
      setPos({ x: r.left + r.width / 2, top: r.top, bottom: r.bottom });
    }
  }

  function hide() {
    setPos(null);
    setNudge(null);
  }

  // Keep the bubble inside the viewport: slide it horizontally, flip it above
  // the dot when there is no room below.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!pos || !el) return;
    const r = el.getBoundingClientRect();
    const m = 10;
    let dx = 0;
    if (r.right > window.innerWidth - m) dx = window.innerWidth - m - r.right;
    if (r.left + dx < m) dx = m - r.left;
    const dy = r.bottom > window.innerHeight - m ? -(r.height + (pos.bottom - pos.top) + 16) : 0;
    setNudge({ dx, dy });
  }, [pos, text, title]);

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={(e) => {
        e.stopPropagation();
        if (pos) hide();
        else show();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        marginLeft: 5,
        borderRadius: '50%',
        border: `1px solid ${C.lineStrong}`,
        color: C.muted,
        font: `600 9px 'IBM Plex Mono',monospace`,
        cursor: 'help',
        verticalAlign: 'middle',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      i
      {pos &&
        createPortal(
          <div
            ref={tipRef}
            style={{
              position: 'fixed',
              left: pos.x,
              top: pos.bottom + 8,
              transform: `translateX(-50%) translate(${nudge?.dx ?? 0}px, ${nudge?.dy ?? 0}px)`,
              visibility: nudge ? 'visible' : 'hidden',
              maxWidth: 260,
              width: 'max-content',
              padding: '11px 13px',
              background: C.elevated,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 10,
              boxShadow: '0 12px 32px rgba(0,0,0,.5)',
              zIndex: 2000,
              pointerEvents: 'none',
              textAlign: 'left',
            }}
          >
            {title && (
              <div
                style={{
                  font: `600 11px 'Outfit',sans-serif`,
                  color: C.primary,
                  marginBottom: 4,
                }}
              >
                {title}
              </div>
            )}
            <div style={{ fontSize: 12, lineHeight: 1.5, color: C.secondary, fontWeight: 400 }}>
              {text}
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}

/** Full-bleed inline error line. */
export function ErrorLine({ children }: { children: ReactNode }) {
  return <div style={{ color: C.act, fontSize: 13, margin: '10px 0' }}>{children}</div>;
}

/** Dashed empty state. */
export function EmptyState({
  glyph = '✦',
  title,
  children,
}: {
  glyph?: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        border: `1px dashed ${C.lineStrong}`,
        borderRadius: 14,
        padding: '48px 40px',
        textAlign: 'center',
        background: C.surface2,
      }}
    >
      <div style={{ fontSize: 30, color: C.lineStrong, marginBottom: 14 }}>{glyph}</div>
      <div style={{ font: `500 15px 'Outfit',sans-serif`, color: C.secondary, marginBottom: 8 }}>
        {title}
      </div>
      {children && (
        <div
          style={{
            fontSize: 12.5,
            color: C.faint,
            maxWidth: 360,
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** "Sample data" chip — marks scaffold screens not yet wired to live data. */
export function SampleTag() {
  return (
    <span
      className="pill"
      style={{
        color: C.info,
        background: alpha(C.info, 13),
        border: `1px solid ${alpha(C.info, 34)}`,
      }}
      title="Placeholder data — wires to live Meta/Copy Engine data in Phase 1"
    >
      Sample data
    </span>
  );
}
